// Tela de Login (/login) — mockup "Login": split hero/formulário, com as
// quatro etapas que o fluxo já tinha (senha, 2FA, confirmação de dispositivo
// e setup obrigatório de 2FA + códigos de recuperação).
//
// Whitelabel: sem `appLogoBackgroundLight/Dark` configurado, o painel
// esquerdo mostra o hero do mockup inteiro. Com imagem, ela vira o fundo do
// painel, com véu escuro por cima e só logo + rodapé — o headline e os
// números saem, para não competir com a arte da empresa.
import React, { useState, useContext, useEffect, useRef } from "react";
import { Link as RouterLink } from "react-router-dom";

import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";
import Checkbox from "@material-ui/core/Checkbox";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import Tooltip from "@material-ui/core/Tooltip";

import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import SecurityIcon from "@material-ui/icons/Security";
import DevicesIcon from "@material-ui/icons/Devices";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import LanguageIcon from "@material-ui/icons/Language";

import { QRCodeSVG } from "qrcode.react";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import ColorModeContext from "../../layout/themeContext";
import useSettings from "../../hooks/useSettings";
import useAerosAuthStyles from "../../styles/aerosAuthStyles";
import AuthBrand from "../../components/AuthBrand";
import { sanitizeOtp, otpDigits, segundosParaReenviar } from "./authRules";
import ResetPasswordSteps from "./ResetPasswordSteps";

import BRFlag from "../../assets/brazil.png";
import USFlag from "../../assets/unitedstates.png";
import ESFlag from "../../assets/esspain.png";
import ARFlag from "../../assets/arabe.png";
import { getBackendUrl } from "../../config";

const languageOptions = [
    { value: "pt-BR", label: "Português", icon: BRFlag },
    { value: "en", label: "English", icon: USFlag },
    { value: "es", label: "Spanish", icon: ESFlag },
    { value: "ar", label: "عربي", icon: ARFlag },
];

// Campos de 6 dígitos do mockup, com avanço/retorno automático.
const OtpInput = ({ value, onChange, classes, autoFocus, onEnter }) => {
    const refs = useRef([]);
    const digits = otpDigits(value);

    const setAt = (index, char) => {
        const next = digits.slice();
        next[index] = char;
        onChange(next.join(""));
    };

    const handleChange = (index) => (e) => {
        const typed = sanitizeOtp(e.target.value, 6);
        if (!typed) {
            setAt(index, "");
            return;
        }
        // Colar o código inteiro numa caixa preenche todas.
        if (typed.length > 1) {
            onChange(typed);
            const last = Math.min(typed.length, 5);
            refs.current[last]?.focus();
            return;
        }
        setAt(index, typed);
        if (index < 5) refs.current[index + 1]?.focus();
    };

    const handleKeyDown = (index) => (e) => {
        if (e.key === "Backspace" && !digits[index] && index > 0) {
            refs.current[index - 1]?.focus();
        }
        if (e.key === "ArrowLeft" && index > 0) refs.current[index - 1]?.focus();
        if (e.key === "ArrowRight" && index < 5) refs.current[index + 1]?.focus();
        if (e.key === "Enter" && onEnter) onEnter();
    };

    return (
        <div className={classes.otpRow}>
            {digits.map((digit, index) => (
                <input
                    key={index}
                    ref={(el) => {
                        refs.current[index] = el;
                    }}
                    className={classes.otpBox}
                    value={digit}
                    inputMode="numeric"
                    autoComplete={index === 0 ? "one-time-code" : "off"}
                    aria-label={`${index + 1}`}
                    autoFocus={autoFocus && index === 0}
                    onChange={handleChange(index)}
                    onKeyDown={handleKeyDown(index)}
                    onFocus={(e) => e.target.select()}
                />
            ))}
        </div>
    );
};

const Login = () => {
    const c = useAerosAuthStyles();
    const ref = useRef(null);

    const { getPublicSetting } = useSettings();
    // O provider expõe { colorMode }; ler o objeto inteiro deixava mode,
    // appName e toggleColorMode indefinidos (nome "pulse" fixo, imagem do tema
    // escuro sempre e botão de tema morto).
    const { colorMode } = useContext(ColorModeContext);
    const { appLogoFavicon, appName, mode } = colorMode;

    const [user, setUser] = useState({ email: "", password: "" });
    const [showPassword, setShowPassword] = useState(false);
    const [allowSignup, setAllowSignup] = useState(false);
    const [rememberDevice, setRememberDevice] = useState(true);

    const {
        handleLogin,
        handleValidate2FA,
        handleConfirmDevice,
        handleSetupMandatory2FA,
        handleVerifyMandatory2FA,
        handleForgotPassword,
        handleVerifyResetCode,
        handleResetPassword,
    } = useContext(AuthContext);

    // 2FA states
    const [twoFactorStep, setTwoFactorStep] = useState(false);
    const [twoFactorSessionToken, setTwoFactorSessionToken] = useState("");
    const [totpCode, setTotpCode] = useState("");

    const [deviceConfirmStep, setDeviceConfirmStep] = useState(false);
    const [deviceConfirmSessionToken, setDeviceConfirmSessionToken] = useState("");
    const [deviceOtp, setDeviceOtp] = useState("");

    // 2FA mandatory setup states
    const [setupStep, setSetupStep] = useState(null); // null, "qr", "recovery"
    const [otpauthUri, setOtpauthUri] = useState("");
    const [setupSecret, setSetupSecret] = useState("");
    const [setupVerifyToken, setSetupVerifyToken] = useState("");
    const [setupLoading, setSetupLoading] = useState(false);
    const [recoveryCodes, setRecoveryCodes] = useState([]);
    const [codesConfirmed, setCodesConfirmed] = useState(false);

    // null | "email" | "code" | "password" — mesma convenção do setupStep.
    const [resetStep, setResetStep] = useState(null);
    const [resetEmail, setResetEmail] = useState("");
    const [resetOtp, setResetOtp] = useState("");
    const [resetSessionToken, setResetSessionToken] = useState("");
    const [resetConfirmToken, setResetConfirmToken] = useState("");
    const [resetRequires2FA, setResetRequires2FA] = useState(false);
    const [resetSentAt, setResetSentAt] = useState(null);
    const [resetSeconds, setResetSeconds] = useState(0);
    const [resetLoading, setResetLoading] = useState(false);

    const [open, setOpen] = useState(false);
    const [enabledLanguages, setEnabledLanguages] = useState(["pt-BR", "en"]);
    const [backgroundLight, setBackgroundLight] = useState("");
    const [backgroundDark, setBackgroundDark] = useState("");
    const [appCopyright, setAppCopyright] = useState("");

    const getCompanyIdFromUrl = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const companyId = urlParams.get("companyId");
        return companyId ? parseInt(companyId) : null;
    };

    const handleChangeInput = (e) => {
        setUser({ ...user, [e.target.name]: e.target.value });
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const handlSubmit = async (e) => {
        e.preventDefault();
        const result = await handleLogin(user);
        if (result && result.requiresTwoFactor) {
            setTwoFactorStep(true);
            setTwoFactorSessionToken(result.twoFactorSessionToken);
        }
        if (result && result.requiresTwoFactorSetup) {
            setTwoFactorSessionToken(result.twoFactorSessionToken);
            setSetupLoading(true);
            try {
                const data = await handleSetupMandatory2FA(result.twoFactorSessionToken);
                setOtpauthUri(data.otpauthUri);
                setSetupSecret(data.secret);
                setSetupStep("qr");
            } catch (err) {
                // error already handled by hook
            }
            setSetupLoading(false);
        }
        if (result && result.requiresDeviceConfirmation) {
            setDeviceConfirmStep(true);
            setDeviceConfirmSessionToken(result.deviceConfirmSessionToken);
        }
    };

    const handleSubmitDeviceConfirm = (e) => {
        if (e) e.preventDefault();
        handleConfirmDevice(deviceConfirmSessionToken, deviceOtp);
    };

    const handleBackDeviceConfirm = () => {
        setDeviceConfirmStep(false);
        setDeviceConfirmSessionToken("");
        setDeviceOtp("");
    };

    const handleSubmit2FA = (e) => {
        if (e) e.preventDefault();
        handleValidate2FA(twoFactorSessionToken, totpCode);
    };

    const handleVerifySetup = async () => {
        if (!setupVerifyToken || setupVerifyToken.length < 6) {
            toast.error(i18n.t("twoFactor.setup.invalidCode"));
            return;
        }
        setSetupLoading(true);
        try {
            const codes = await handleVerifyMandatory2FA(
                twoFactorSessionToken,
                setupVerifyToken
            );
            setRecoveryCodes(codes);
            setSetupStep("recovery");
        } catch (err) {
            // error already handled by hook
        }
        setSetupLoading(false);
    };

    const handleCopyCodes = () => {
        navigator.clipboard.writeText(recoveryCodes.join("\n"));
        toast.success(i18n.t("twoFactor.setup.codesCopied"));
    };

    const handleCopySecret = () => {
        navigator.clipboard.writeText(setupSecret || "");
        toast.success(i18n.t("twoFactor.setup.codesCopied"));
    };

    const handleBack2FA = () => {
        setTwoFactorStep(false);
        setTwoFactorSessionToken("");
        setTotpCode("");
        setSetupStep(null);
    };

    useEffect(() => {
        if (resetSentAt === null) {
            setResetSeconds(0);
            return undefined;
        }
        const tick = () => setResetSeconds(segundosParaReenviar(resetSentAt, Date.now()));
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [resetSentAt]);

    const abrirReset = () => {
        setResetEmail(user.email || "");
        setResetOtp("");
        setResetStep("email");
    };

    const fecharReset = () => {
        setResetStep(null);
        setResetOtp("");
        setResetSessionToken("");
        setResetConfirmToken("");
        setResetRequires2FA(false);
        setResetSentAt(null);
    };

    const enviarCodigoReset = async () => {
        setResetLoading(true);
        try {
            const token = await handleForgotPassword(resetEmail);
            setResetSessionToken(token);
            setResetSentAt(Date.now());
            setResetStep("code");
        } catch (err) {
            // toastError já foi disparado pelo hook.
        } finally {
            setResetLoading(false);
        }
    };

    const reenviarCodigoReset = async () => {
        setResetLoading(true);
        try {
            const token = await handleForgotPassword(resetEmail);
            setResetSessionToken(token);
            setResetSentAt(Date.now());
        } catch (err) {
            // idem
        } finally {
            setResetLoading(false);
        }
    };

    const conferirCodigoReset = async () => {
        setResetLoading(true);
        try {
            const data = await handleVerifyResetCode(resetSessionToken, sanitizeOtp(resetOtp));
            setResetConfirmToken(data.passwordResetConfirmToken);
            setResetRequires2FA(Boolean(data.requiresTwoFactor));
            setResetStep("password");
        } catch (err) {
            // idem
        } finally {
            setResetLoading(false);
        }
    };

    const salvarSenhaNova = async (senha, totpCode) => {
        setResetLoading(true);
        try {
            await handleResetPassword(resetConfirmToken, senha, totpCode);
            const email = resetEmail;
            fecharReset();
            setUser((prev) => ({ ...prev, email, password: "" }));
            toast.success(i18n.t("login.reset.success"));
        } catch (err) {
            // idem
        } finally {
            setResetLoading(false);
        }
    };

    useEffect(() => {
        const companyId = getCompanyIdFromUrl();

        getPublicSetting("userCreation", companyId)
            .then((data) => {
                setAllowSignup(data === "enabled");
            })
            .catch((error) => {
                console.log("Error reading setting", error);
            });

        getPublicSetting("appCopyright", companyId)
            .then((value) => setAppCopyright(value || ""))
            .catch(() => setAppCopyright(""));

        getPublicSetting("enabledLanguages", companyId)
            .then((langs) => {
                let arr = ["pt-BR", "en"];
                try {
                    if (langs) arr = JSON.parse(langs);
                } catch {}
                setEnabledLanguages(arr);
            })
            .catch(() => {
                setEnabledLanguages(["pt-BR", "en"]);
            });

        getPublicSetting("appLogoBackgroundLight", companyId)
            .then((bgLight) => {
                setBackgroundLight(bgLight ? `${getBackendUrl()}/public/${bgLight}` : "");
            })
            .catch(() => setBackgroundLight(""));

        getPublicSetting("appLogoBackgroundDark", companyId)
            .then((bgDark) => {
                setBackgroundDark(bgDark ? `${getBackendUrl()}/public/${bgDark}` : "");
            })
            .catch(() => setBackgroundDark(""));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const current =
        languageOptions.find((opt) => opt.value === i18n.language) ||
        languageOptions[0];

    const handleSelect = (opt) => {
        i18n.changeLanguage(opt.value);
        localStorage.setItem("language", opt.value);
        setOpen(false);
        window.location.reload();
    };

    // Imagem de whitelabel do tema atual, quando houver.
    const heroImage =
        mode === "light" ? backgroundLight?.trim() : backgroundDark?.trim();
    const hasHeroImage = Boolean(heroImage);

    const renderHero = () => (
        <div
            className={c.hero}
            style={hasHeroImage ? { backgroundImage: `url(${heroImage})` } : undefined}
        >
            {hasHeroImage ? (
                <div className={c.heroVeil} />
            ) : (
                <>
                    <div className={c.heroBlobPurple} />
                    <div className={c.heroBlobCyan} />
                    <div className={c.heroGrid} />
                </>
            )}

            <AuthBrand classes={c} appName={appName} appLogoFavicon={appLogoFavicon} />

            {/* Com imagem de whitelabel o texto continua: só o véu por cima
                muda. Esconder o headline fazia a tela "perder" os textos
                segundos depois de abrir, quando a configuração chegava. */}
            <div className={c.heroBody}>
                <div className={c.heroEyebrow}>{i18n.t("login.hero.eyebrow")}</div>
                <div className={c.heroTitle}>{i18n.t("login.hero.title")}</div>
                <div className={c.heroText}>{i18n.t("login.hero.text")}</div>
                <div className={c.heroStats}>
                    {[1, 2, 3].map((n) => (
                        <div key={n} className={c.heroStat}>
                            <div className={c.heroStatValue}>
                                {i18n.t(`login.hero.stat${n}Value`)}
                            </div>
                            <div className={c.heroStatLabel}>
                                {i18n.t(`login.hero.stat${n}Label`)}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className={c.heroFooter}>
                {appCopyright ? (
                    <span>
                        © {new Date().getFullYear()} {appCopyright}
                    </span>
                ) : null}

                <div style={{ marginLeft: "auto", position: "relative" }} ref={ref}>
                    <button
                        type="button"
                        className={c.backLink}
                        onClick={() => setOpen((o) => !o)}
                        aria-haspopup="listbox"
                        aria-expanded={open}
                    >
                        <LanguageIcon />
                        {current.label}
                    </button>
                    {open && (
                        <div
                            style={{
                                position: "absolute",
                                bottom: "calc(100% + 8px)",
                                right: 0,
                                background: "#12151F",
                                border: "1px solid rgba(255,255,255,0.12)",
                                borderRadius: 10,
                                padding: 4,
                                minWidth: 150,
                                zIndex: 20,
                            }}
                            role="listbox"
                        >
                            {languageOptions
                                .filter((opt) => enabledLanguages.includes(opt.value))
                                .map((opt) => (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        onClick={() => handleSelect(opt)}
                                        className={c.backLink}
                                        style={{ padding: "7px 10px", width: "100%" }}
                                    >
                                        <img
                                            src={opt.icon}
                                            alt=""
                                            style={{ width: 16, height: 11, objectFit: "cover" }}
                                        />
                                        {opt.label}
                                    </button>
                                ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    // ---- etapa: setup obrigatório de 2FA (QR + chave + código) ----
    const renderSetupQr = () => (
        <div className={c.formCard}>
            <div className={c.progressTrack}>
                <div className={`${c.progressSegment} ${c.progressSegmentOn}`} />
                <div className={c.progressSegment} />
            </div>
            <div>
                <div className={c.mandatoryBadge}>{i18n.t("login.setup.mandatory")}</div>
                <div className={c.formTitleSmall + " " + c.formTitle} style={{ marginTop: 6 }}>
                    {i18n.t("login.setup.title")}
                </div>
                <div className={c.formSubtitle}>{i18n.t("login.setup.subtitle")}</div>
            </div>

            <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                <div className={c.qrBox}>
                    {otpauthUri ? <QRCodeSVG value={otpauthUri} size={120} /> : null}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div className={c.formSubtitle} style={{ marginTop: 0, marginBottom: 6 }}>
                        {i18n.t("login.setup.manual")}
                    </div>
                    <div className={c.secretBox}>{setupSecret}</div>
                    <button type="button" className={c.copyLink} onClick={handleCopySecret}>
                        <FileCopyOutlinedIcon />
                        {i18n.t("login.setup.copyKey")}
                    </button>
                </div>
            </div>

            <div>
                <label className={c.label} htmlFor="setup-code">
                    {i18n.t("login.setup.confirmCode")}
                </label>
                <TextField
                    id="setup-code"
                    variant="outlined"
                    className={c.input}
                    value={setupVerifyToken}
                    onChange={(e) => setSetupVerifyToken(sanitizeOtp(e.target.value))}
                    placeholder="000000"
                    inputProps={{ maxLength: 6, inputMode: "numeric" }}
                    autoFocus
                />
            </div>

            <Button
                fullWidth
                variant="contained"
                color="primary"
                className={c.gradientButton}
                onClick={handleVerifySetup}
                disabled={setupLoading || setupVerifyToken.length < 6}
            >
                {setupLoading ? (
                    <CircularProgress size={22} />
                ) : (
                    i18n.t("twoFactor.setup.verifyAndActivate")
                )}
            </Button>

            <button type="button" className={c.backLink} onClick={handleBack2FA}>
                <ArrowBackIcon />
                {i18n.t("login.twoFactor.back")}
            </button>
        </div>
    );

    // ---- etapa: códigos de recuperação ----
    const renderRecovery = () => (
        <div className={c.formCard}>
            <div className={c.progressTrack}>
                <div className={`${c.progressSegment} ${c.progressSegmentOn}`} />
                <div className={`${c.progressSegment} ${c.progressSegmentOn}`} />
            </div>
            <div>
                <div className={c.formTitle} style={{ fontSize: 24 }}>
                    {i18n.t("login.recovery.title")}
                </div>
                <div className={c.formSubtitle}>
                    {i18n.t("twoFactor.setup.recoveryWarning")}
                </div>
            </div>

            <div className={c.recoveryGrid}>
                {recoveryCodes.map((code, idx) => (
                    <div key={idx} className={c.recoveryCode}>
                        {code}
                    </div>
                ))}
            </div>

            <Button
                fullWidth
                variant="outlined"
                className={c.squareButton}
                style={{ width: "100%", height: 40, borderRadius: 10 }}
                onClick={handleCopyCodes}
                startIcon={<FileCopyOutlinedIcon />}
            >
                {i18n.t("twoFactor.setup.copyCodes")}
            </Button>

            <label
                style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}
            >
                <Checkbox
                    color="primary"
                    checked={codesConfirmed}
                    onChange={(e) => setCodesConfirmed(e.target.checked)}
                    style={{ padding: 0 }}
                />
                {i18n.t("twoFactor.setup.confirmSaved")}
            </label>

            <div className={c.footNote}>{i18n.t("twoFactor.setup.setupComplete")}</div>
        </div>
    );

    // ---- etapa: 2FA / confirmação de dispositivo ----
    const renderOtpStep = ({
        icon,
        title,
        subtitle,
        value,
        onChange,
        onSubmit,
        onBack,
        submitLabel,
        footer,
    }) => (
        <form
            className={c.formCard}
            noValidate
            onSubmit={(e) => {
                e.preventDefault();
                onSubmit();
            }}
        >
            <button type="button" className={c.backLink} onClick={onBack}>
                <ArrowBackIcon />
                {i18n.t("login.twoFactor.back")}
            </button>

            <div className={c.stepIcon}>{icon}</div>

            <div>
                <div className={c.formTitle} style={{ fontSize: 24 }}>
                    {title}
                </div>
                <div className={c.formSubtitle}>{subtitle}</div>
            </div>

            <OtpInput
                value={value}
                onChange={onChange}
                classes={c}
                autoFocus
                onEnter={onSubmit}
            />

            <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                className={c.gradientButton}
                disabled={sanitizeOtp(value).length < 6}
            >
                {submitLabel}
            </Button>

            {footer}
        </form>
    );

    // ---- etapa: e-mail e senha ----
    const renderPasswordStep = () => (
        <form className={c.formCard} noValidate onSubmit={handlSubmit}>
            <div>
                <div className={c.formTitle}>{i18n.t("login.pageTitle")}</div>
                <div className={c.formSubtitle}>{i18n.t("login.pageSubtitle")}</div>
            </div>

            <div className={c.fields}>
                <div>
                    <label className={c.label} htmlFor="email">
                        {i18n.t("login.form.email")}
                    </label>
                    <TextField
                        id="email"
                        name="email"
                        variant="outlined"
                        className={c.input}
                        value={user.email}
                        onChange={handleChangeInput}
                        autoComplete="email"
                        autoFocus
                        required
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <MailOutlineIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                </div>

                <div>
                    <div className={c.label}>
                        <label htmlFor="password">{i18n.t("login.form.password")}</label>
                        <button
                            type="button"
                            className={c.labelAside}
                            onClick={abrirReset}
                        >
                            {i18n.t("login.forgotPassword")}
                        </button>
                    </div>
                    <TextField
                        id="password"
                        name="password"
                        variant="outlined"
                        className={c.input}
                        type={showPassword ? "text" : "password"}
                        value={user.password}
                        onChange={handleChangeInput}
                        autoComplete="current-password"
                        required
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <LockOutlinedIcon />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label={i18n.t(
                                            showPassword ? "login.hidePassword" : "login.showPassword"
                                        )}
                                        onClick={togglePasswordVisibility}
                                        edge="end"
                                        size="small"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </div>
            </div>

            <label
                style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}
            >
                <Checkbox
                    color="primary"
                    checked={rememberDevice}
                    onChange={(e) => setRememberDevice(e.target.checked)}
                    style={{ padding: 0 }}
                />
                {i18n.t("login.rememberDevice")}
            </label>

            <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                className={c.gradientButton}
            >
                {i18n.t("login.buttons.submit")}
            </Button>

            {allowSignup && (
                <div className={c.footNote}>
                    {i18n.t("login.noAccount")}{" "}
                    <RouterLink to="/signup" className={c.ghostLink}>
                        {i18n.t("login.createAccount")}
                    </RouterLink>
                </div>
            )}
        </form>
    );

    const renderStep = () => {
        if (resetStep) {
            return (
                <ResetPasswordSteps
                    step={resetStep}
                    classes={c}
                    OtpInput={OtpInput}
                    email={resetEmail}
                    onEmailChange={setResetEmail}
                    otp={resetOtp}
                    onOtpChange={setResetOtp}
                    requiresTwoFactor={resetRequires2FA}
                    resendSeconds={resetSeconds}
                    onSend={enviarCodigoReset}
                    onVerify={conferirCodigoReset}
                    onSubmit={salvarSenhaNova}
                    onResend={reenviarCodigoReset}
                    onBack={fecharReset}
                    loading={resetLoading}
                />
            );
        }
        if (setupStep === "qr") return renderSetupQr();
        if (setupStep === "recovery") return renderRecovery();
        if (deviceConfirmStep) {
            return renderOtpStep({
                icon: <DevicesIcon />,
                title: i18n.t("login.deviceConfirm.title"),
                subtitle: i18n.t("login.deviceConfirm.subtitle"),
                value: deviceOtp,
                onChange: setDeviceOtp,
                onSubmit: handleSubmitDeviceConfirm,
                onBack: handleBackDeviceConfirm,
                submitLabel: i18n.t("login.deviceConfirm.verify"),
            });
        }
        if (twoFactorStep) {
            return renderOtpStep({
                icon: <SecurityIcon />,
                title: i18n.t("login.twoFactor.title"),
                subtitle: i18n.t("login.twoFactor.subtitle"),
                value: totpCode,
                onChange: setTotpCode,
                onSubmit: handleSubmit2FA,
                onBack: handleBack2FA,
                submitLabel: i18n.t("login.twoFactor.verify"),
                footer: (
                    <div className={c.footNote} style={{ fontSize: 12.5 }}>
                        {i18n.t("login.twoFactor.noApp")}{" "}
                        <Tooltip title={i18n.t("aeros.flow.editor.soon")}>
                            <span
                                className={c.ghostLink}
                                style={{ opacity: 0.5, cursor: "default" }}
                            >
                                {i18n.t("login.twoFactor.useRecovery")}
                            </span>
                        </Tooltip>
                    </div>
                ),
            });
        }
        return renderPasswordStep();
    };

    return (
        <>
            <Helmet>
                <title>{appName || "Pulse"}</title>
                <link rel="icon" href={appLogoFavicon || "/default-favicon.ico"} />
            </Helmet>

            <div className={c.root}>
                {renderHero()}

                <div className={c.formPane}>
                    <div className={c.paneTopRight}>
                        <Tooltip title={i18n.t("login.toggleTheme")}>
                            <IconButton
                                className={c.squareButton}
                                onClick={colorMode.toggleColorMode}
                                aria-label={i18n.t("login.toggleTheme")}
                            >
                                {mode === "dark" ? <Brightness7Icon /> : <Brightness4Icon />}
                            </IconButton>
                        </Tooltip>
                    </div>

                    {renderStep()}
                </div>
            </div>
        </>
    );
};

export default Login;
